package top.wcpe.allincore.ui.runtime.event

/**
 * 鼠标事件。
 *
 * DSL 作者在 `onMouseDown / onMouseUp / onMouseMove { event -> ... }` handler 内可调用 [consume]
 * 标记本事件已被消费——框架据此决定是否阻止 vanilla 继续处理。未调用 [consume] 时事件仅被「监听」，
 * 不影响 vanilla 行为。
 *
 * @param x 鼠标 X 坐标（屏幕/根坐标系）
 * @param y 鼠标 Y 坐标
 * @param button 按键编号：0=左键，1=右键，2=中键，3..6=侧键；-1 表示由 hotkey 触发的合成事件
 * @param buttonName 字符串按键名（真源为 platform-client-api 的 MouseButtons，commonMain 侧见其镜像
 * [MouseButtonNames]），便于 DSL 中按 `event.button == "Right"` 判断
 */
class MouseEvent(x: Double, y: Double, button: Int, buttonName: String) {

    private val x: Double = x
    private val y: Double = y
    private val button: Int = button
    private val buttonName: String = buttonName

    // 本地坐标（命中元素 box 左上角为原点），未经 dispatch 填充时默认与屏幕坐标一致
    private var localX: Double = x
    private var localY: Double = y

    /**
     * 是否已被消费。Java 侧以 `isConsumed()` 方法式查询，Kotlin 侧以 `event.isConsumed` 属性式读取
     * （HudInputHook / EventMap 各自消费），仅可经 [consume] 置位。
     */
    var isConsumed: Boolean = false
        private set

    /**
     * 兼容入口：仅传入 button 编号，自动通过 [MouseButtonNames.fromCode] 推导 buttonName。
     *
     * 保留此构造让旧调用方零修改即可工作；新代码建议直接用 4 参构造以减少一次查表。
     */
    constructor(x: Double, y: Double, button: Int) : this(x, y, button, MouseButtonNames.fromCode(button))

    fun x(): Double = x

    fun y(): Double = y

    /**
     * 本地坐标 X（命中元素 box 的左上角为 (0,0)）。由 [InputDispatcher] 在分发 onClick / onRightClick /
     * onPointerMove 等回调前用 HitTester 命中元素 absBox 计算填充；直接构造而不经 dispatch 时默认与 [x] 相同。
     */
    fun localX(): Double = localX

    fun localY(): Double = localY

    /** 框架内部使用：[InputDispatcher] 在 dispatchClick 等方法内根据命中元素 absBox 填充本地坐标。 */
    fun setLocal(localX: Double, localY: Double) {
        this.localX = localX
        this.localY = localY
    }

    fun button(): Int = button

    fun buttonName(): String = buttonName

    /**
     * DSL 作者在 handler 内调用：标记本事件已被消费，阻止 vanilla 继续处理。
     *
     * 典型用法：
     * ```
     * onMouseDown { event ->
     *     if (event.button == "Left") {
     *         event.consume()
     *     }
     * }
     * ```
     */
    fun consume() {
        isConsumed = true
    }
}
